import { supabase } from '../../core/config/supabase';
import { FollowEntity, FollowWithUserEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class FollowDataSource {
  async create(followerId: string, followingId: string): Promise<FollowEntity> {
    const { data, error } = await supabase
      .from('follows')
      .insert({ follower_id: followerId, following_id: followingId })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as FollowEntity;
  }

  async delete(followerId: string, followingId: string): Promise<void> {
    const { error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', followerId)
      .eq('following_id', followingId);

    if (error) throw new Error(error.message);
  }

  async getFollowers(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<FollowWithUserEntity[]> {
    logger.debug('FollowDataSource', 'Calling RPC', { functionName: 'get_followers' });

    const { data, error } = await supabase.rpc('get_followers', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('FollowDataSource', 'getFollowers failed', { error });
      throw new Error(error.message);
    }

    logger.debug('FollowDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as FollowWithUserEntity[]) || [];
  }

  async getFollowing(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<FollowWithUserEntity[]> {
    logger.debug('FollowDataSource', 'Calling RPC', { functionName: 'get_following' });

    const { data, error } = await supabase.rpc('get_following', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('FollowDataSource', 'getFollowing failed', { error });
      throw new Error(error.message);
    }

    logger.debug('FollowDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as FollowWithUserEntity[]) || [];
  }

  async isFollowing(followerId: string, followingId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return data !== null;
  }
}
